import { RbacRolePills } from "newui/features/Settings/components/RolesPermissions/RbacRolePills";
import type { UserAssignment } from "newui/features/RolesPermissions/types/RolesPermissions.type";
import type { UserListItem } from "../../../types/userInvite";
import { UserStatusBadge } from "./UserStatusBadge";
import { UserRowActions } from "./UserRowActions";

interface UserListRowProps {
  user: UserListItem;
  /** PRO-709: false = the row is read-only (no row-click edit, no actions). */
  canManageUsers?: boolean;
  onDeactivate: (user: UserListItem) => void;
  onEdit: (user: UserListItem) => void;
  // PRO-413: RBAC v2 additive layer (see UserListTable).
  rbacEnabled?: boolean;
  assignment?: UserAssignment | null;
  onManageRoles?: (user: UserListItem) => void;
  onViewAccess?: (user: UserListItem) => void;
}

const formatLastActivity = (user: UserListItem): string => {
  const date = user.registered_at ?? user.invited_at;
  if (!date) {
    return "—";
  }
  return new Date(date).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export const UserListRow = ({
  user,
  onEdit,
  onDeactivate,
  canManageUsers = true,
  rbacEnabled = false,
  assignment = null,
  onManageRoles,
  onViewAccess,
}: UserListRowProps) => (
  <tr
    className={
      canManageUsers
        ? "cursor-pointer border-b border-gray-200 hover:bg-gray-50"
        : "border-b border-gray-200"
    }
    onClick={canManageUsers ? () => onEdit(user) : undefined}
    data-testid={`user-row-${user.id}`}
  >
    <td className="px-4 py-3 text-sm text-gray-900">
      {user.first_name} {user.last_name}
    </td>
    <td className="px-4 py-3 text-sm text-gray-500">{user.email}</td>
    <td className="px-4 py-3 text-sm text-gray-500">
      {rbacEnabled ? (
        <RbacRolePills assignment={assignment} />
      ) : (
        <span className="whitespace-nowrap">
          {user.role?.display_name ?? user.role?.name ?? "—"}
        </span>
      )}
    </td>
    <td className="whitespace-nowrap px-4 py-3 text-sm">
      <UserStatusBadge status={user.status} inviteExpiresAt={user.invite_expires_at} />
    </td>
    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
      {formatLastActivity(user)}
    </td>
    {canManageUsers && (
      <td className="whitespace-nowrap px-4 py-3 text-right text-sm">
        <UserRowActions
          user={user}
          onEdit={onEdit}
          onDeactivate={onDeactivate}
          canManageUsers={canManageUsers}
          rbacEnabled={rbacEnabled}
          onManageRoles={onManageRoles}
          onViewAccess={onViewAccess}
        />
      </td>
    )}
  </tr>
);
